import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FaBarcode, FaPix, FaRegFileCode, FaRegFilePdf } from 'react-icons/fa6';
import { Col, Row } from 'react-bootstrap';
import isEmpty from 'lodash/isEmpty';
import { LuArrowRight, LuArrowUpRight, LuCreditCard } from 'react-icons/lu';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';

import FORMATTERS from 'helpers/formatters';
import { Button, EmptyMessage } from '_components/_core';
import { useTableV2 } from '_components/_core/Table/utils';
import { ItemsPerPage, Pagination } from '_components/_core/Table/components';

import {
  CustomCard,
  CustomCardBody,
  CustomCardHeader,
  CustomBreadcrumb,
  CustomBreadcrumbItem,
} from '../../../../styles';

import { StyledTable } from './styles';
import { LoadingIcon } from '_components/_shared';

const tableConfig = {
  defaultFilters: {},
  defaultSorting: { field: 'due_date', order: 'desc' },
  defaultPagination: { currentPage: 1, itemsPerPage: 10 },
};

const CYCLE_MONTHS = {
  1: 'Mensal',
  6: 'Semestral',
  12: 'Anual',
};

const PAYMENT_METHOD_ICON = {
  CREDIT_CARD: (
    <LuCreditCard
      data-tip="Pago com cartão"
      data-place="bottom"
      className="ml-2 mr-1 text-success"
    />
  ),
  PIX: (
    <FaPix
      data-tip="Pago com PIX"
      data-place="bottom"
      className="ml-2 mr-1 text-success"
    />
  ),
  BOLETO: (
    <FaBarcode
      data-tip="Pago com boleto"
      data-place="bottom"
      className="ml-2 mr-1 text-success"
    />
  ),
  UNDEFINED: <LuCreditCard className="ml-2 mr-1 text-success" />,
};

function InvoiceHistory({
  onFetchSubscriptionInvoices,
  onChangeView,
  subscriptionInvoices,
  isLoading,
  withBreadcrumb,
  originView,
  subscription,
}) {
  const { provider_name } = subscription || {};

  const { pagination, onPageChange, onPageSizeChange } = useTableV2({
    ...tableConfig,
    data: subscriptionInvoices.data,
    onFetchData: onFetchSubscriptionInvoices,
    withSavedFilters: false,
    withInitialLoading: true,
  });

  const renderStatusIcon = useCallback(invoice => {
    switch (invoice.status) {
      case 'paid':
        return (
          <FaCheckCircle
            size="1rem"
            className="text-success ml-2"
            data-place="bottom"
            data-tip={`Paga em ${FORMATTERS.DATE_DDMMYYYY(invoice.paid_at)}`}
          />
        );
      case 'overdue':
        return (
          <FaExclamationCircle
            size="1rem"
            className="text-danger ml-2"
            data-place="bottom"
            data-tip="Pagamento atrasado"
          />
        );
      default:
        return null;
    }
  }, []);

  const renderActionButton = useCallback(
    invoice => {
      const { paid_at, status } = invoice;

      const isPending = ['open', 'overdue'].includes(status);
      const isInvalid = ['void', 'uncollectible', 'draft'].includes(status);

      if (isInvalid) {
        return <div className="d-flex align-items-center justify-content-center">-</div>;
      }

      if (paid_at) {
        return (
          <div className="d-flex align-items-center justify-content-center">
            {FORMATTERS.DATE_DDMMYYYY(invoice.paid_at) || '-'}
            {PAYMENT_METHOD_ICON[invoice.payment_method]}
          </div>
        );
      }

      if (isPending && provider_name === 'ASAAS') {
        return (
          <div className="d-flex align-items-center justify-content-center">
            <Button
              variant="outline-primary"
              size="sm"
              className="p-0 m-0 d-flex align-items-center justify-content-center pl-3 pr-3 pt-2 pb-2"
              onClick={() => window.open(invoice.provider_invoice_url, '_blank')}
              style={{
                borderRadius: '6px',
              }}
            >
              Pagar
              <LuArrowUpRight className="ml-2" />
            </Button>
          </div>
        );
      }

      if (isPending && provider_name === 'STRIPE') {
        return (
          <div className="d-flex align-items-center justify-content-center">
            <Button
              variant="secondary"
              size="sm"
              className="p-0 m-0 d-flex align-items-center justify-content-center pl-2 pr-2 pt-1 pb-1"
              onClick={() =>
                onChangeView('INVOICE_DETAILS', {
                  invoice,
                  invoiceId: invoice.id,
                  originView: originView || 'INVOICE_HISTORY',
                })
              }
            >
              Detalhes
              <LuArrowRight className="ml-2" />
            </Button>
          </div>
        );
      }

      return null;
    },
    [onChangeView, provider_name, originView],
  );

  return (
    <>
      {withBreadcrumb && (
        <CustomBreadcrumb>
          <CustomBreadcrumbItem onClick={() => onChangeView('DEFAULT')}>
            Assinatura
          </CustomBreadcrumbItem>
          <CustomBreadcrumbItem active>Histórico de faturas</CustomBreadcrumbItem>
        </CustomBreadcrumb>
      )}
      <ReactTooltip />
      <Row>
        <Col>
          <CustomCard>
            <CustomCardHeader>
              <h3>Histórico de faturas</h3>
            </CustomCardHeader>
            <CustomCardBody noPadding className="p-3">
              {isLoading && <LoadingIcon text="Carregando..." className="mt-5 mb-5" />}
              <div className="table-responsive">
                {!isEmpty(subscriptionInvoices.data) && !isLoading && (
                  <StyledTable className="table table-hover">
                    <thead>
                      <tr>
                        <th width="100px" className="text-center">
                          Fatura
                        </th>
                        <th width="100px" className="text-center">
                          Pagamento
                        </th>
                        <th width="140px" className="text-center">
                          Vencimento
                        </th>
                        <th width="100px" className="text-center">
                          Valor
                        </th>
                        <th className="text-left">Descrição</th>
                        <th width="100px" className="text-center">
                          Situação
                        </th>
                        <th width="50px" className="text-center">
                          Nota Fiscal
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptionInvoices.data.map((invoice, index) => {
                        const nextIndex = index + 1;
                        const nextInvoice = subscriptionInvoices.data[nextIndex];
                        let displayCancelledRow = false;

                        if (nextInvoice) {
                          if (
                            nextInvoice.subscription_id !== invoice.subscription_id &&
                            ['canceled', 'incomplete_expired'].includes(
                              nextInvoice.subscription_status,
                            )
                          ) {
                            displayCancelledRow = true;
                          }
                        }

                        return (
                          <>
                            <tr
                              key={invoice.id}
                              className={classNames({
                                borderDanger: invoice.status === 'overdue',
                              })}
                            >
                              <td width="100px" className="text-center">
                                <strong>{invoice.provider_invoice_number || '-'}</strong>
                              </td>
                              <td width="100px" className="text-center">
                                {renderActionButton(invoice)}
                              </td>
                              <td width="140px" className="text-left">
                                <span className="d-flex align-items-center justify-content-center">
                                  {FORMATTERS.DATE_DDMMYYYY(invoice.due_date)}
                                  {renderStatusIcon(invoice)}
                                </span>
                              </td>
                              <td width="100px" className="text-center">
                                <strong>{FORMATTERS.NUMBER(invoice.total_amount)}</strong>
                              </td>
                              <td className="text-left">
                                <Button
                                  variant="link"
                                  className="p-0 m-0"
                                  size="sm"
                                  onClick={() =>
                                    onChangeView('INVOICE_DETAILS', {
                                      invoice,
                                      invoiceId: invoice.id,
                                      originView: originView || 'INVOICE_HISTORY',
                                    })
                                  }
                                >
                                  {`${invoice.description} ${CYCLE_MONTHS[invoice.subscription_cycle_months]}`}
                                </Button>
                              </td>

                              <td className="text-center">
                                {FORMATTERS.SUBSCRIPTION_INVOICE_STATUS(invoice.status)}
                              </td>
                              <td width="50px" className="text-center">
                                <div className="d-flex align-items-center justify-content-center">
                                  <a
                                    target="_blank"
                                    href={invoice.nfse_pdf_url}
                                    rel="noreferrer"
                                    className="d-flex align-items-center justify-content-center"
                                  >
                                    <FaRegFilePdf size="20" />
                                  </a>
                                  <a
                                    target="_blank"
                                    href={invoice.nfse_xml_url}
                                    rel="noreferrer"
                                    className="ml-3 d-flex align-items-center justify-content-center"
                                  >
                                    <FaRegFileCode size="20" />
                                  </a>
                                </div>
                              </td>
                            </tr>
                            {displayCancelledRow && (
                              <tr className="clear">
                                <td
                                  colSpan={8}
                                  className="text-center text-danger p-0 pt-2 pb-2"
                                >
                                  <small style={{ fontWeight: 500, fontSize: 12 }}>
                                    Assinatura cancelada em{' '}
                                    {FORMATTERS.DATE_DDMMYYYY(
                                      nextInvoice.subscription_canceled_at,
                                    )}
                                  </small>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                    </tbody>
                  </StyledTable>
                )}
              </div>
              {isEmpty(subscriptionInvoices.data) && !isLoading && (
                <EmptyMessage title="Nenhuma fatura encontrada" />
              )}
            </CustomCardBody>
          </CustomCard>
        </Col>
      </Row>
      <Row className="mt-3 mt-md-0">
        <Col xs={12} md={6} className="d-flex justify-content-start">
          <ItemsPerPage
            itemsPerPage={pagination.itemsPerPage}
            onChange={onPageSizeChange}
            noMarginsOnTotals
            className="mr-3"
            total={subscriptionInvoices.total}
            totalBeingShown={subscriptionInvoices.data.length}
            maxItemsPerPage={100}
          />
        </Col>
        <Col xs={12} md={6} className="d-flex justify-content-end">
          <Pagination
            {...pagination}
            total={subscriptionInvoices.total}
            onPageChange={onPageChange}
          />
        </Col>
      </Row>
    </>
  );
}

InvoiceHistory.defaultProps = {
  payments: [],
  activeCompany: {},
  subscriptionInvoices: {
    total: 0,
    data: [],
  },
  isLoading: false,
  withBreadcrumb: true,
};

InvoiceHistory.propTypes = {
  payments: PropTypes.array,
  onFetchSubscriptionInvoices: PropTypes.func,
  activeCompany: PropTypes.object,
  onChangeView: PropTypes.func,
  subscriptionInvoices: PropTypes.object,
  isLoading: PropTypes.bool,
  withBreadcrumb: PropTypes.bool,
  originView: PropTypes.string,
};

export default InvoiceHistory;
